import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

function parseTimeToMinutes(t) {
  if (!t) return null;
  // Backend may send "HH:MM:SS" or "HH:MM"
  const parts = String(t).split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function sumCompletedHours(logs) {
  let total = 0;
  for (const l of logs) {
    // Not-payable sessions (free/compensation classes) do not consume hours —
    // matches the payment and cycle-deduction logic on the backend.
    if (l.is_payable === false) continue;
    if (l.status === 'completed') {
      const s = parseTimeToMinutes(l.start_time);
      const e = parseTimeToMinutes(l.end_time);
      // Count the REAL duration (start → end). No 1-hour fallback: a session
      // with missing times contributes 0 rather than silently inflating the
      // total to 1 per session.
      if (s !== null && e !== null && e > s) {
        total += (e - s) / 60;
      }
    } else if (l.cancel_reason === 'student_absent') {
      // Student-absent: the tutor attended but the student didn't, so the
      // billing cycle charges a flat 0.5h. Count it here too so the report's
      // hours total matches the Payment & Hours figure exactly.
      total += 0.5;
    }
  }
  return Math.round(total * 100) / 100;
}

// Load an image, downscale it to a fixed bounding box, and return a JPEG data
// URL. We cap the longest side at MAX_PX so an oversized brand logo doesn't
// balloon the resulting PDF — a 4000x4000 source PNG was previously embedding
// at full resolution and inflating reports past 40 MB.
async function loadImageBase64(url, maxPx = 256, quality = 0.85) {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const longest = Math.max(img.width, img.height) || 1;
      const scale = Math.min(1, maxPx / longest);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      // White background — JPEG has no alpha and logos often use transparency.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function addWatermark(doc) {
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.07 }));
    doc.setTextColor(0, 60, 150);
    doc.setFontSize(36);
    doc.setFont('helvetica', 'bold');
    // Four diagonal strings per page is enough to deter casual copying without
    // bloating the PDF stream — the old 5x3 grid wrote 15 strings per page.
    doc.text('SAPIENCE EDU CONNECT', 30, 80,  { angle: 45 });
    doc.text('SAPIENCE EDU CONNECT', 30, 200, { angle: 45 });
    doc.text('SAPIENCE EDU CONNECT', 130, 80,  { angle: 45 });
    doc.text('SAPIENCE EDU CONNECT', 130, 200, { angle: 45 });
    doc.restoreGraphicsState();
  }
}

export async function generateStudentPDF({ profile, logs, grades, hours, feedback = [], dateRange = null }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const blue = [37, 84, 184];
  const textDark = [15, 31, 69];
  const muted = [100, 116, 139];
  const W = 210;

  let logoBase64 = null;
  try { logoBase64 = await loadImageBase64('/logo.png'); } catch {}

  doc.setFillColor(...blue);
  doc.rect(0, 0, W, 38, 'F');

  if (logoBase64) {
    doc.addImage(logoBase64, 'JPEG', 8, 4, 28, 28);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('SAPIENCE EDU CONNECT', logoBase64 ? 42 : 12, 17);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Connecting Minds, Shaping Futures', logoBase64 ? 42 : 12, 24);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Student Progress Report', W - 12, 17, { align: 'right' });
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, W - 12, 24, { align: 'right' });
  if (dateRange) doc.text(`Period: ${dateRange}`, W - 12, 30, { align: 'right' });

  let y = 46;
  doc.setFillColor(240, 245, 255);
  doc.roundedRect(10, y, W - 20, 28, 3, 3, 'F');
  doc.setTextColor(...textDark);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(profile?.name || 'Student', 16, y + 9);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...muted);
  const infoLine = [
    profile?.class_name && `Class: ${profile.class_name}`,
    profile?.syllabus && `Syllabus: ${profile.syllabus}`,
    profile?.package && `Package: ${profile.package}`,
    profile?.selected_subjects && `Subjects: ${profile.selected_subjects}`,
  ].filter(Boolean).join('   ·   ');
  doc.text(infoLine, 16, y + 17);
  // Hours line: when the report has a date range, show hours summed from
  // completed logs in that range (period-correct). Otherwise show the live
  // billing-cycle counter.
  if (dateRange) {
    const periodHours = sumCompletedHours(logs);
    const completedCount = logs.filter(l => l.status === 'completed').length;
    doc.setTextColor(...blue);
    doc.setFont('helvetica', 'bold');
    doc.text(`Hours in period: ${periodHours.toFixed(1)} hrs across ${completedCount} completed session${completedCount === 1 ? '' : 's'}`, 16, y + 24);
  } else if (hours?.billing_type === 'monthly') {
    doc.setTextColor(...blue);
    doc.setFont('helvetica', 'bold');
    doc.text(`Hours used in ${hours.month_label}: ${hours.this_month_hours} hrs`, 16, y + 24);
  } else if (hours?.hours_per_month > 0) {
    doc.setTextColor(...blue);
    doc.setFont('helvetica', 'bold');
    doc.text(`Paid hours: ${hours.hours_used} / ${hours.hours_per_month} hrs used${hours.cycle_start ? `  (since ${hours.cycle_start})` : ''}`, 16, y + 24);
  }
  y += 34;

  doc.setTextColor(...textDark);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Class Attendance & Progress', 10, y);
  y += 5;

  // Every visible session type is listed: completed, tutor/student leave,
  // emergency, student absent. Not-payable sessions are filtered out upstream
  // (they exist only for payment) so they never appear here.
  const statusLabel = (l) => (
    l.cancel_reason === 'tutor_leave' ? 'Tutor Leave' :
    l.cancel_reason === 'student_leave' ? 'Student Leave' :
    l.cancel_reason === 'student_absent' ? 'Student Absent' :
    l.cancel_reason === 'emergency' ? 'Emergency' :
    (l.status === 'completed' ? 'Completed' :
     l.status === 'cancelled' ? 'Cancelled' : l.status)
  );

  // Duration of a session from its scheduled start → end time, e.g. "1h", "1h 30m".
  const durationLabel = (l) => {
    const s = parseTimeToMinutes(l.start_time);
    const e = parseTimeToMinutes(l.end_time);
    if (s === null || e === null || e <= s) return '—';
    const mins = e - s;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return (h ? `${h}h` : '') + (m ? `${h ? ' ' : ''}${m}m` : (h ? '' : `${mins}m`));
  };

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Subject', 'Companion', 'Topics Covered', 'Homework', 'Duration', 'Status']],
    body: logs.map(l => [
      l.schedule_date || l.updated_at?.slice(0, 10) || '—',
      l.subject || '—',
      l.tutor_name || '—',
      l.topics || '—',
      l.homework || '—',
      durationLabel(l),
      statusLabel(l),
    ]),
    headStyles: { fillColor: blue, textColor: 255, fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: textDark },
    alternateRowStyles: { fillColor: [248, 250, 255] },
    columnStyles: { 3: { cellWidth: 38 }, 4: { cellWidth: 34 }, 5: { cellWidth: 16 } },
    margin: { left: 10, right: 10 },
    didDrawPage: () => {},
  });

  y = doc.lastAutoTable.finalY + 10;

  const completed = logs.filter(l => l.status === 'completed').length;
  const cancelled = logs.filter(l => l.status === 'cancelled').length;
  const tutorLeave = logs.filter(l => l.cancel_reason === 'tutor_leave').length;
  const studentLeave = logs.filter(l => l.cancel_reason === 'student_leave').length;
  const totalSessions = logs.length;
  const attendancePct = totalSessions ? Math.round((completed / totalSessions) * 100) : 0;

  if (y > 250) { doc.addPage(); y = 20; }
  doc.setTextColor(...textDark);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Monthly Summary', 10, y);
  y += 5;
  autoTable(doc, {
    startY: y,
    body: [
      ['Total Sessions', String(totalSessions)],
      ['Completed', String(completed)],
      ['Class Hours (excl. free sessions)', String(sumCompletedHours(logs))],
      ['Cancelled (Tutor Leave)', String(tutorLeave)],
      ['Cancelled (Student Leave)', String(studentLeave)],
      ['Other Cancellations', String(cancelled - tutorLeave - studentLeave)],
      ['Attendance %', `${attendancePct}%`],
    ],
    bodyStyles: { fontSize: 9, textColor: textDark },
    columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold' }, 1: { cellWidth: 30, halign: 'right' } },
    margin: { left: 10, right: 10 },
  });
  y = doc.lastAutoTable.finalY + 10;

  if (feedback && feedback.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setTextColor(...textDark);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Companion Feedback', 10, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      head: [['Month', 'Subject', 'Companion', 'Progress', 'Improvements', 'Focus Areas', 'Chapters']],
      body: feedback.map(f => [f.month, f.subject, f.tutor_name || '—', f.progress_summary || '—', f.improvements || '—', f.focus_areas || '—', f.chapters_completed || '—']),
      headStyles: { fillColor: [22, 163, 74], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: textDark },
      alternateRowStyles: { fillColor: [240, 253, 244] },
      columnStyles: { 3: { cellWidth: 30 }, 4: { cellWidth: 28 }, 5: { cellWidth: 28 }, 6: { cellWidth: 25 } },
      margin: { left: 10, right: 10 },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  const graded = grades.filter(g => g.marks_obtained !== null);
  if (graded.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setTextColor(...textDark);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Test Results', 10, y);
    y += 5;

    autoTable(doc, {
      startY: y,
      head: [['Test Name', 'Subject', 'Date', 'Marks', 'Max', '%', 'Result', 'Remarks']],
      body: graded.map(g => {
        const pct = Math.round((g.marks_obtained / g.max_marks) * 100);
        return [g.test_name, g.subject, g.test_date, g.marks_obtained, g.max_marks, `${pct}%`, pct >= 40 ? 'Pass' : 'Fail', g.remarks || '—'];
      }),
      headStyles: { fillColor: [124, 58, 237], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: textDark },
      alternateRowStyles: { fillColor: [250, 248, 255] },
      didDrawCell: (data) => {
        if (data.column.index === 6 && data.section === 'body') {
          const pass = data.cell.raw === 'Pass';
          doc.setTextColor(pass ? 22 : 220, pass ? 163 : 38, pass ? 74 : 38);
        }
      },
      margin: { left: 10, right: 10 },
    });
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(...blue);
    doc.rect(0, 287, W, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Sapience Edu Connect  ·  Connecting Minds, Shaping Futures', 10, 293);
    doc.text(`Page ${i} of ${totalPages}`, W - 10, 293, { align: 'right' });
  }

  addWatermark(doc);
  doc.save(`${profile?.name || 'Student'}_Progress_Report.pdf`);
}
